import { useState, useEffect } from 'react'

import styles from '../styles/login.module.css'
import '../styles/style.css'

import Icon from '../components/icon'
import useGoogleOAuth from '../hooks/useGoogleOAuth'
import useApplicationAuth from '../hooks/useApplicationAuth'
import { Checkbox, CircularProgress } from "@mui/material"
import { Alert } from '@mui/material'
import Logo from '../components/logo/logo'
import TurnstileForm from '../components/cloudflare/turnstile'
import { getMe } from '../api-client/data'
import useFetch from '../hooks/useFetch'

const SignInForm = ({ onSubmit, onNavigateCreateAccount }) => {
    const [cloudflareToken, setCloudflareToken] = useState()

    return (
        <>
            <div className={styles.loginHeader}>
                <h1>Sign in</h1>
            </div>
            <div className={styles.loginFormContainer}>
                <form onSubmit={onSubmit}>
                    <div className={styles.loginInputGroup}>
                        <input name="email" placeholder='Email Address' />
                        <input name="password" type="password" placeholder='Password' />
                    </div>

                    <div className={styles.centerForm}>
                        <TurnstileForm setToken={setCloudflareToken} />
                    </div>

                    <button type="submit" className={styles.continueButton} disabled={cloudflareToken === undefined}>Log in</button>
                </form>

                <div className={styles.noAccount}>Don't have an account? <span onClick={onNavigateCreateAccount}>Create One</span></div>
            </div>
        </>
    )
}

const CreateAccountForm = ({ onSubmit, setResponse }) => {
    const [checked, setChecked] = useState(false)
    const [cloudflareToken, setCloudflareToken] = useState()

    const onLocalSubmit = (e) => {
        e.preventDefault()
        if (!checked) {
            setResponse({
                status: 'error',
                message: "Terms of service and privacy policy must be accepted"
            })
            return
        }

        onSubmit(e)
    }

    return (
        <>
            <div className={styles.loginHeader}>
                <h1>Create an Account</h1>
            </div>
            <div className={styles.loginFormContainer}>
                <form onSubmit={onLocalSubmit}>
                    <div className={styles.loginInputGroup}>
                        <input name="name" placeholder='Name' />
                        <input name="email" placeholder='Email Address' />
                        <input name="password" type="password" placeholder='Password' />
                        <input name="confirmPassword" type="password" placeholder='Confirm Password' />
                    </div>

                    <div className={styles.centerForm}>
                        <TurnstileForm setToken={setCloudflareToken} />
                    </div>

                    <div className={styles.terms}>
                        <Checkbox checked={checked} onChange={(event) => setChecked(event?.target?.checked)} />
                        <div>
                            By creating an account, you agree to our <a target="_blank" rel="noopener noreferrer" href="https://flow.nuraml.com/tos">Terms of Service</a> and have read our <a href="https://flow.nuraml.com/privacy" rel="noopener noreferrer" target="_blank">Privacy Policy</a>
                        </div>
                    </div>

                    <button type="submit" className={styles.continueButton} disabled={cloudflareToken === undefined}>Create an Account</button>
                </form>
            </div>
        </>
    )
}

const EmailNotificationForm = ({ onSubmit }) => (
    <>
        <div className={styles.loginHeader}>
            <h1>Verify Your Email</h1>
            <h3>Please input the code sent to your email</h3>
        </div>
        <div className={styles.loginFormContainer}>
            <form onSubmit={onSubmit}>
                <div className={styles.loginInputGroup}>
                    <input name="code" placeholder='Code' />
                </div>

                <button type="submit" className={styles.continueButton}>Continue</button>
            </form>
        </div>
    </>
)

const AccountResetFormPassword = ({ onSubmit }) => (
    <>
        <div className={styles.loginHeader}>
            <h1>Reset Password</h1>
        </div>

        <div className={styles.loginFormContainer}>
            <form onSubmit={onSubmit}>
                <div className={styles.loginInputGroup}>
                    <input name="password" type="password" placeholder='Password' />
                    <input name="confirmPassword" type="password" placeholder='Confirm Password' />
                </div>

                <button type="submit" className={styles.continueButton}>Reset</button>
            </form>
        </div>
    </>
)

const AccountResetFormEmail = ({ onSubmit }) => (
    <>
        <div className={styles.loginHeader}>
            <h1>Reset Password</h1>
        </div>

        <div className={styles.loginFormContainer}>
            <form onSubmit={onSubmit}>
                <div className={styles.loginInputGroup}>
                    <input name="email" placeholder='Email' />
                </div>

                <button type="submit" className={styles.continueButton}>Reset</button>
            </form>
        </div>
    </>
)

const AccountResetForm = ({
    applicationAuth,
    onComplete,
    wrapFormControl,
    status,
    setStatus,
    verificationId,
    setVerificationId,
}) => {
    const onSubmitAccountResetEmail = wrapFormControl(async (e) => {
        const formData = new FormData(e.target)
        const formValues = Object.fromEntries(formData.entries())

        const { verificationId } = await applicationAuth.initiateEmailVerificationCode(formValues?.email)
        setVerificationId(verificationId)
        setStatus(1)
    })

    const onSubmitEmailNotificationForm = wrapFormControl(async (e) => {
        const formData = new FormData(e.target)
        const formValues = Object.fromEntries(formData.entries())

        const status = await applicationAuth.onIsCodeValid(verificationId, formValues?.code)
        if (status === true) {
            setStatus(2)
        }
    })

    const onSubmitPasswordChangeForm = wrapFormControl(async (e) => {
        await applicationAuth.onResetPassword(e, verificationId)
        onComplete()
    })

    switch (status) {
        case 0:
            return (
                <AccountResetFormEmail onSubmit={onSubmitAccountResetEmail} />
            )
        case 1:
            return (
                <EmailNotificationForm onSubmit={onSubmitEmailNotificationForm} />
            )
        case 2:
            return (
                <AccountResetFormPassword onSubmit={onSubmitPasswordChangeForm} />
            )
        default:
            return null
    }
}

function DotsBackground({ children }) {
    return <div className={styles.landingBackground}>{children}</div>
}

const Login = () => {
    const { signIn } = useGoogleOAuth()
    const applicationAuth = useApplicationAuth()

    const [loading, setIsLoading] = useState(false)
    const [isResetForm, setIsResetForm] = useState(false)
    const [isCreateUser, setIsCreateUser] = useState(false)
    const [resetStatus, setResetStatus] = useState(0)
    const [verificationId, setVerificationId] = useState()

    const me = useFetch(getMe)
    useEffect(() => {
        if (me.data?.name!==undefined && me.data?.name?.length > 0) {
            window.location.href = "/"
        }
    }, [me.data])

    useEffect(() => {
        applicationAuth.resetMessage()
    }, [resetStatus])

    const wrapFormControl = (fn) => async (e) => {
        e.preventDefault()
        setIsLoading(true)
        try {
            await fn(e)
        } catch { }
        setIsLoading(false)
    }

    const navigateCreateAccount = () => {
        setIsCreateUser(true)
    }

    const handleBackButton = () => {
        setIsResetForm(false)
        setIsCreateUser(false)
        setVerificationId(false)
        setIsLoading(false)
        setResetStatus(0)
    }

    const onComplete = () => {
        setIsResetForm(false)
        setIsCreateUser(false)
    }

    const onSubmitSignin = wrapFormControl(e => applicationAuth.onSignInFormSubmit(e))
    const submitEmailNotificationForm = wrapFormControl(async (e) => {
        const status = await applicationAuth.onSubmitEmailVerificationCode(e)
        if (status === true) {
            window.location.href = "/"
        }
    })
    const onHandleCreateAccount = wrapFormControl(async (e) => {
        await applicationAuth.onSubmitCreateUser(e)
        onComplete()
    })

    return (
        <div className={styles.page}>
            <div className={styles.leftSide}>
                <DotsBackground>
                    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
                        <div style={{ padding: 20 }}>
                            <Logo textColor="white" />
                        </div>

                        <div className={styles.thing}>
                            <div className={styles.thingOne}></div>
                            <div className={styles.thingTwo}></div>
                            <div className={styles.thingThree}></div>
                        </div>
                        <div className={styles.thingThingTwo}>
                            <div className={styles.thingOne}></div>
                            <div className={styles.thingTwo}></div>
                            <div className={styles.thingThree}></div>
                        </div>
                    </div>
                </DotsBackground>
            </div>
            <div className={styles.rightSide}>
                {
                    !!applicationAuth.response.message && (
                        <Alert
                            severity={applicationAuth.response.status}
                        >
                            {applicationAuth.response.message}
                        </Alert>
                    )
                }

                <div className={styles.login}>
                    {
                        ({
                            [true]: <SignInForm onNavigateCreateAccount={navigateCreateAccount} onSubmit={onSubmitSignin} />,
                            [isCreateUser]: <CreateAccountForm onSubmit={onHandleCreateAccount} setResponse={applicationAuth.setResponse} />,
                            [isResetForm === true]: <AccountResetForm wrapFormControl={wrapFormControl} applicationAuth={applicationAuth} onComplete={onComplete} setStatus={setResetStatus} status={resetStatus} verificationId={verificationId} setVerificationId={setVerificationId} />,
                            [applicationAuth.shouldVerifyEmail]: <EmailNotificationForm onSubmit={submitEmailNotificationForm} />,
                            [loading]: <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}><CircularProgress /></div>,
                        }).true
                    }
                    <div className={styles.formSpacer}>
                        <span>OR</span>
                    </div>
                    <div>
                        <button className={styles.oauthProvider} onClick={signIn}>
                            <div className={styles.oauthIcon}>
                                <Icon type="google" />
                            </div>
                            <div className={styles.oauthTitle}>
                                Sign in with Google
                            </div>
                        </button>
                        {({
                            [true]: (
                                <div className={styles.forgotPassword} onClick={() => setIsResetForm(true)}>
                                    <span>
                                        Forgot Password
                                    </span>
                                </div>
                            ),
                            [isResetForm || isCreateUser]: (
                                <div className={styles.forgotPassword} onClick={handleBackButton}>
                                    <span>
                                        Back
                                    </span>
                                </div>
                            ),
                        }).true}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Login